import api from './axiosConfig';
import { Topic, ReviewItem, Package, VocabularyReview, TopicLearning, PackageLearning, AiDefinition, VocabularyWord } from '../types';

/**
 * Gửi IdToken của Google đến backend để xác thực.
 */
export const loginWithGoogle = async (idToken: string) => {
  const response = await api.post('/Auth/google-login', { idToken });
  return response.data; // Trả về { token, user }
};

/**
 * Lấy tất cả các Package từ backend.
 */
export async function fetchPackagesLearning(): Promise<PackageLearning[]> {
  const response = await api.get('/Packages');
  return response.data;
}

/**
 * Lấy một chủ đề cụ thể bằng ID.
 */
export async function fetchPackageLearningById(packageId: string): Promise<PackageLearning> {
  const response = await api.get(`/Packages/${packageId}`);
  return response.data;
}

/**
 * Lấy tất cả các chủ đề từ backend.
 */
export async function fetchTopics(): Promise<Topic[]> {
  const response = await api.get('/Topics');
  return response.data;
}

/**
 * Lấy một chủ đề cụ thể bằng ID.
 */
export async function fetchTopicReviewById(topicId: string): Promise<VocabularyReview[]> {
  const response = await api.get(`/Topics/${topicId}`);
  return response.data;
}

/**
 * Gửi kết quả review của người dùng.
 */
export async function submitReview(vocabId: string, userDefinition: string, isNew: number) {
  const response = await api.post('/reviews', { 
    vocabularyId: vocabId, 
    userDefinition, 
    isNew 
  });
  return response.data;
}

/**
 * Lấy danh sách từ vựng để review cho một chủ đề.
 */
export async function fetchVocabsForReviewByTopicId(topicId: string, reviewType:string): Promise<ReviewItem[] | undefined> {
  const response = await api.get(`/reviews/${topicId}?reviewType=${reviewType}`);
  return response.data;
}

export const lookupWordWithAI = async (word: string): Promise<AiDefinition> => {
  const response = await api.post('/DictionaryAi', { word });
  return response.data;
};


export const fetchTopicsByPackageId = async (packageId: string): Promise<Topic[]> => {
  const response = await api.get(`/Topics/by-package/${packageId}`);
  return response.data;
};


export const fetchVocabulariesByTopicId = async (topicId: string): Promise<VocabularyWord[]> => {
  const response = await api.get(`/Vocabularies/by-topic/${topicId}`);
  return response.data;
};

export async function fetchPackages(): Promise<Package[]> {
  const response = await api.get('/Packages');
  return response.data;
}

export const searchImagesForWord = async (word: string, wordId: string): Promise<string[]> => {
  const response = await api.get(`/ImageSearch?word=${word}&wordId=${wordId}`);
  return response.data; // Ví dụ: ['url1.jpg', 'url2.jpg', ...]
};

// --- CREATE Functions ---
export const createPackage = async (data: Omit<Package, 'id'>): Promise<Package> => {
  const response = await api.post('/Packages', data);
  return response.data;
};

export const createTopic = async (data: Omit<Topic, 'id'>): Promise<Topic> => {
  const response = await api.post('/Topics', data);
  return response.data;
};

export const createVocabulary = async (data: Omit<VocabularyWord, 'id'>) => {
  return await api.post('/Vocabularies', data);
};

export const createVocabulariesBatch = async (vocabularies: Omit<VocabularyWord, 'id'>[]) => {
  return await api.post('/Vocabularies/bulk-create', vocabularies);
};

// --- UPDATE Functions ---
export const updatePackage = async (packageId: string, data: Omit<Package, 'id'>) => {
  return await api.put(`/Packages/${packageId}`, data);
};

export const updateTopic = async (topicId: string, data: Omit<Topic, 'id'>) => {
  return await api.put(`/Topics/${topicId}`, data);
};

export const updateVocabulary = async (vocabId: string, data: Omit<VocabularyWord, 'id'>) => {
  return await api.put(`/Vocabularies/${vocabId}`, data);
};

// --- DELETE Functions ---
export const deletePackage = async (packageId: string) => {
  return await api.delete(`/Packages/${packageId}`);
};

export const deleteTopic = async (topicId: string) => {
  return await api.delete(`/Topics/${topicId}`);
};

export const deleteVocabulary = async (vocabId: string) => {
  return await api.delete(`/Vocabularies/${vocabId}`);
};

export const saveDictationProgress = async (vocabularyId: string, exampleNumber: 1 | 2, status: 'correct') => {
  return await api.post('/reviews/dictation', { vocabularyId, exampleNumber, status });
};